import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { MODULES } from "./content";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);

  const links = [
    { href: "#product", label: "Product" },
    { href: "#how-it-works", label: "How it works" },
    { href: "#pricing", label: "Pricing" },
    { href: "#faq", label: "FAQ" },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-zinc-200/80 transition-all">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" aria-label="Optics home" className="flex items-center gap-2">
          <Logo wordmark="Optics" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-7 text-xs font-semibold uppercase tracking-wider text-zinc-600" aria-label="Main">
          <div
            className="relative"
            onMouseEnter={() => setProductOpen(true)}
            onMouseLeave={() => setProductOpen(false)}
          >
            <a
              href="#product"
              className="inline-flex items-center gap-1 hover:text-zinc-900 transition-colors py-2"
            >
              Product <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${productOpen ? "rotate-180" : ""}`} />
            </a>

            {productOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[540px]">
                <div className="bg-white border border-zinc-200 rounded-2xl shadow-xl p-4 grid grid-cols-2 gap-2 text-left normal-case tracking-normal">
                  {MODULES.slice(0, 8).map((m) => (
                    <a
                      key={m.name}
                      href="#product"
                      onClick={() => setProductOpen(false)}
                      className="flex items-start gap-3 rounded-xl p-2.5 hover:bg-zinc-50 transition-colors group"
                    >
                      <div className="h-8 w-8 rounded-lg bg-zinc-100 group-hover:bg-zinc-900 grid place-items-center shrink-0 transition-colors">
                        <m.icon className="h-4 w-4 text-zinc-600 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-zinc-900">{m.name}</div>
                        <div className="text-[11px] text-zinc-500 line-clamp-1 mt-0.5">{m.desc}</div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a href="#how-it-works" className="hover:text-zinc-900 transition-colors">
            How it works
          </a>
          <a href="#pricing" className="hover:text-zinc-900 transition-colors">
            Pricing
          </a>
          <a href="#faq" className="hover:text-zinc-900 transition-colors">
            FAQ
          </a>
        </nav>

        {/* Action Buttons */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            data-testid="nav-login"
            to="/login"
            className="text-xs font-semibold text-zinc-700 hover:text-zinc-900 px-3 py-2 transition-colors"
          >
            Sign In
          </Link>

          <Link
            to="/login"
            className="text-xs font-semibold inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 rounded-xl transition-all shadow-sm hover:shadow"
          >
            Start Free Trial <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden p-2 text-zinc-700 hover:text-zinc-900"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile Drawer */}
      {open && (
        <div className="md:hidden border-t border-zinc-200 bg-white px-6 py-4 space-y-2">
          {links.map((a) => (
            <a
              key={a.href}
              href={a.href}
              onClick={() => setOpen(false)}
              className="block py-2 text-sm font-medium text-zinc-700 hover:text-zinc-900"
            >
              {a.label}
            </a>
          ))}
          <div className="pt-4 flex flex-col gap-2 border-t border-zinc-100 mt-2">
            <Link
              data-testid="nav-login"
              to="/login"
              className="text-center text-sm font-medium text-zinc-700 py-2 border border-zinc-200 rounded-xl"
            >
              Sign In
            </Link>
            <Link
              to="/login"
              className="text-center text-sm font-semibold bg-zinc-900 text-white py-2.5 rounded-xl shadow"
            >
              Start Free 14-Day Trial
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
